import { type FC, type ReactNode } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  LinearProgress,
  type LinearProgressProps,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { BroadcastStateType } from "@/types/graphql/partner.generated";
import {
  broadcastRates,
  formatPercent,
  formatUsd,
  SKIP_REASON_KEYS,
  type AutomationDetails,
} from "@/partner/entities/marketing";

interface FunnelRow {
  key: string;
  label: string;
  hint?: string;
  value: string;
  secondary?: string;
  /** Доля для полосы; null или отсутствие значит строка без полосы. */
  percent?: number | null;
  barColor?: LinearProgressProps["color"];
}

/**
 * Результаты рассылки: воронка «отправлено, доставлено, открыли, кликнули»,
 * отписки, просмотры и покупки по ссылкам письма, пропущенные адресаты по
 * причинам. Во время отправки сверху ход обработки.
 */
export const BroadcastStatsCard: FC<{ automation: AutomationDetails }> = ({
  automation,
}) => {
  const { t, i18n } = useTranslation();
  const { stats, state } = automation;
  const rates = broadcastRates(stats);
  const percent = (value: number | null) =>
    value === null
      ? t("broadcasts.stats.noData")
      : formatPercent(value, i18n.language);
  const done = stats.completed + stats.exited;
  const skipped = stats.skipped.filter((item) => item.count > 0);

  const rows: FunnelRow[] = [
    {
      key: "sent",
      label: t("broadcasts.stats.sent"),
      value: String(stats.sent),
    },
    {
      key: "delivered",
      label: t("broadcasts.stats.delivered"),
      value: `${stats.delivered} · ${percent(rates.delivery)}`,
      percent: rates.delivery,
    },
    {
      key: "opened",
      label: t("broadcasts.stats.opened"),
      hint: t("broadcasts.stats.openHint"),
      value: `${stats.opened} · ${percent(rates.open)}`,
      percent: rates.open,
    },
    {
      key: "clicked",
      label: t("broadcasts.stats.clicked"),
      value: `${stats.clicked} · ${percent(rates.click)}`,
      secondary:
        rates.clickToOpen === null
          ? undefined
          : t("broadcasts.stats.clickToOpen", {
              value: formatPercent(rates.clickToOpen, i18n.language),
            }),
      percent: rates.click,
    },
    {
      key: "unsubscribed",
      label: t("broadcasts.stats.unsubscribed"),
      value: `${stats.unsubscribed} · ${percent(rates.unsubscribe)}`,
      percent: rates.unsubscribe,
      barColor: "warning",
    },
    {
      key: "views",
      label: t("broadcasts.stats.views"),
      value: String(stats.views),
      secondary: t("broadcasts.stats.viewers", { count: stats.viewers }),
    },
    {
      key: "purchases",
      label: t("broadcasts.stats.purchases"),
      value: `${stats.purchases} · ${percent(rates.conversion)}`,
    },
    {
      key: "revenue",
      label: t("broadcasts.stats.revenue"),
      value: formatUsd(stats.revenueUSD, i18n.language),
      secondary:
        rates.revenuePerEmail === null
          ? undefined
          : t("broadcasts.stats.perEmail", {
              value: formatUsd(rates.revenuePerEmail, i18n.language),
            }),
    },
    {
      key: "renewals",
      label: t("broadcasts.stats.renewals"),
      value: String(stats.renewals),
      secondary: t("broadcasts.stats.withRenewals", {
        value: formatUsd(stats.revenueWithRenewalsUSD, i18n.language),
      }),
    },
  ];

  const renderRow = (row: FunnelRow, index: number): ReactNode => (
    <ListItem
      key={row.key}
      disableGutters
      divider={index < rows.length - 1}
      // Одна высота у всех строк: с полосой и без неё.
      sx={{ minHeight: 64, pr: 16 }}
      secondaryAction={
        <Stack alignItems="flex-end">
          <Typography variant="body2">{row.value}</Typography>
          {row.secondary && (
            <Typography variant="caption" color="text.secondary">
              {row.secondary}
            </Typography>
          )}
        </Stack>
      }
    >
      <ListItemText
        disableTypography
        primary={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2">{row.label}</Typography>
            {row.hint && (
              <Tooltip title={row.hint}>
                <InfoOutlinedIcon fontSize="inherit" color="action" />
              </Tooltip>
            )}
          </Stack>
        }
        secondary={
          typeof row.percent === "number" ? (
            <LinearProgress
              variant="determinate"
              value={row.percent}
              color={row.barColor ?? "primary"}
              sx={{ mt: 1 }}
            />
          ) : null
        }
      />
    </ListItem>
  );

  return (
    <Card elevation={0}>
      <CardHeader
        title={t("broadcasts.stats.title")}
        subheader={
          stats.sent > 0 && stats.delivered === 0
            ? t("broadcasts.stats.deliveryPending")
            : undefined
        }
        sx={{ px: 2, pt: 2, pb: 0 }}
      />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        <Stack spacing={2}>
          {state === BroadcastStateType.SendingType && (
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                {t("broadcasts.stats.progress", {
                  done,
                  total: stats.enrolled,
                })}
              </Typography>
              <LinearProgress
                variant="determinate"
                color="warning"
                value={
                  stats.enrolled > 0
                    ? Math.min(100, (done / stats.enrolled) * 100)
                    : 0
                }
              />
            </Stack>
          )}
          <List disablePadding>{rows.map(renderRow)}</List>
          {skipped.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                {t("broadcasts.stats.skipped")}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {skipped.map((item) => {
                  const key = SKIP_REASON_KEYS[item.reason];
                  return (
                    <Chip
                      key={item.reason}
                      size="small"
                      label={`${key ? t(key) : item.reason}: ${item.count}`}
                    />
                  );
                })}
              </Stack>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
